import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "../lib/api";

const AppContext = createContext(null);

export const AppProvider = ({ children }) => {
  const [parent, setParent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [language, setLanguage] = useState(localStorage.getItem("bn_lang") || "en");
  const [settings, setSettings] = useState({
    sound_level: "low",
    motion: "reduced",
    contrast: "standard",
    font: "standard",
    bedtime_mode: false,
    reminders_enabled: false,
    reminder_time: null,
    quiet_days: [],
    browser_push_enabled: false,
  });

  const applySettings = useCallback((s, bedtime) => {
    const html = document.documentElement;
    html.classList.toggle("bedtime", !!bedtime);
    html.classList.toggle("dyslexia", s.font === "dyslexia");
    html.classList.toggle("high-contrast", s.contrast === "high");
    html.classList.toggle("reduced-motion", s.motion === "reduced");
  }, []);

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setParent(data);
      setLanguage(data.language || "en");
      if (data.settings) {
        setSettings(data.settings);
        applySettings(data.settings, data.settings.bedtime_mode);
      }
    } catch {
      setParent(null);
      localStorage.removeItem("bn_token");
    } finally {
      setLoading(false);
    }
  }, [applySettings]);

  useEffect(() => {
    if (localStorage.getItem("bn_token")) {
      refreshMe();
    } else {
      setLoading(false);
    }
  }, [refreshMe]);

  useEffect(() => {
    applySettings(settings, settings.bedtime_mode);
    localStorage.setItem("bn_lang", language);
  }, [settings, language, applySettings]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("bn_token", data.token);
    await refreshMe();
    return data.parent;
  }, [refreshMe]);

  const register = useCallback(async (name, email, password) => {
    const { data } = await api.post("/auth/register", { name, email, password });
    localStorage.setItem("bn_token", data.token);
    await refreshMe();
    return data.parent;
  }, [refreshMe]);

  const logout = useCallback(() => {
    localStorage.removeItem("bn_token");
    setParent(null);
  }, []);

  const updateSettings = useCallback(async (next) => {
    setSettings((prev) => {
      const merged = { ...prev, ...next };
      applySettings(merged, merged.bedtime_mode);
      return merged;
    });
    try {
      await api.put("/settings", { ...next, language });
    } catch {
      // gentle no-op
    }
  }, [applySettings, language]);

  const updateLanguage = useCallback(async (lang) => {
    setLanguage(lang);
    localStorage.setItem("bn_lang", lang);
    try {
      await api.put("/settings", { language: lang });
    } catch {}
  }, []);

  // Apply per-child sensory overrides without touching the parent doc.
  // Each non-null child sensory_* field overrides the parent setting visually.
  // Preserves the current bedtime class state (don't fight with a route that set it).
  const applyChildSettings = useCallback((child) => {
    if (!child) return;
    const merged = {
      ...settings,
      ...(child.sensory_sound ? { sound_level: child.sensory_sound } : {}),
      ...(child.sensory_motion ? { motion: child.sensory_motion } : {}),
      ...(child.sensory_contrast ? { contrast: child.sensory_contrast } : {}),
      ...(child.sensory_font ? { font: child.sensory_font } : {}),
    };
    const currentBedtime = document.documentElement.classList.contains("bedtime");
    applySettings(merged, currentBedtime);
  }, [settings, applySettings]);

  const restoreParentSettings = useCallback(() => {
    applySettings(settings, settings.bedtime_mode);
  }, [settings, applySettings]);

  // Memoize the context value so consumers don't re-render unnecessarily
  // when an unrelated piece of state changes elsewhere in the tree.
  const value = useMemo(() => ({
    parent,
    setParent,
    loading,
    language,
    setLanguage: updateLanguage,
    settings,
    updateSettings,
    login,
    register,
    logout,
    refreshMe,
    applyChildSettings,
    restoreParentSettings,
  }), [
    parent, loading, language, settings,
    updateLanguage, updateSettings, login, register, logout, refreshMe,
    applyChildSettings, restoreParentSettings,
  ]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = () => useContext(AppContext);
export default AppContext;
